import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';

import { Subject } from 'rxjs';
import { debounceTime, first, takeUntil } from 'rxjs/operators';

import { ToolsHelper } from '@app/core/helpers/tools.helper';
import { ClusterModel } from '@app/core/models/cluster.model';
import {
  AnalyticsEvents,
  AnalyticsService,
  APP_CONFIG,
  EventsService,
  IAppConfig,
  SearchService,
  StorageService,
} from '@app/core/services';
import { ClusterApiService, PlaceApiService } from '@app/shared/services/api';

// import AutocompletePrediction = google.maps.places.AutocompletePrediction;

enum ResultType {
  Destination,
  Place,
}

interface IResultItem {
  type?: ResultType;
  title?: string;
  address?: string;
  image?: string;
  id?: string;
  slug?: string;
}

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
})
export class SearchComponent implements OnInit, OnDestroy {
  headerHaveBreadcrumb = false;
  showResults = false;
  searchInput: UntypedFormControl;
  results: Array<IResultItem> = [];
  suggestions: Array<IResultItem> = [];
  inputNotFocused = true;

  private clusters: Array<ClusterModel> = [];

  private destroy$: Subject<void> = new Subject();

  constructor(
    @Inject(APP_CONFIG) private readonly appConfig: IAppConfig,
    private clusterApiService: ClusterApiService,
    private placeApiService: PlaceApiService,
    private storageService: StorageService,
    private searchService: SearchService,
    private eventsService: EventsService,
    private router: Router,
    private analyticsService: AnalyticsService,
  ) {
    this.searchService.foundedPlaces$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.results = [];
        if (result) {
          for (const value of result) {
            this.results.push({
              type: this.checkType(value.types),
              title: value.structured_formatting.main_text,
              address: value.structured_formatting.secondary_text,
              image: this.checkExists(value.place_id),
              id: value.place_id,
            } as IResultItem);
          }
        }
      });
    this.searchInput = new UntypedFormControl();
  }

  ngOnInit(): void {
    const clusters = this.storageService.CLUSTERS;
    if (!clusters) {
      this.clusterApiService
        .getClusters()
        .pipe(first(), takeUntil(this.destroy$))
        .subscribe((response: Array<ClusterModel>) => {
          this.compileComponent(response);
        });
    } else {
      this.compileComponent(clusters);
    }

    this.searchInput.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((value: string) => {
        if (value.length > 2) {
          this.searchService.search(value);
          this.analyticsService.emitEvent(
            AnalyticsEvents.navigationSearchTyping,
            {},
          );
        }
      });
    this.eventsService.clearSearch$
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.searchInput.patchValue('');
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  showSuggestions(): void {
    this.analyticsService.emitEvent(
      AnalyticsEvents.navigationSearchActivation,
      {},
    );
    this.showResults = true;
    this.headerHaveBreadcrumb = false;
    this.eventsService.isSearchMinimalSet(false);
  }

  onClickedOutside(): void {
    this.showResults = false;
  }

  checkType(value: Array<string>): ResultType {
    let type = ResultType.Place;
    for (const item of value) {
      if (item === 'locality') {
        type = ResultType.Destination;
      }
    }
    return type;
  }

  checkExists(value: string): string {
    let image = '';
    for (const cluster of this.clusters) {
      for (const id of cluster.placeIds) {
        if (id.toString() === value) {
          image = cluster.image ? cluster.image : null;
        }
      }
    }
    return image;
  }

  goToSuggestion(item: IResultItem): void {
    this.router.navigate(['/destination', item.slug]);
    this.showResults = false;
    this.eventsService.closeMobileMenuValueSet(true);
  }

  goToDetails(id: string, name: string, type: ResultType): void {
    let exists = false;
    let selectedCluster: ClusterModel = new ClusterModel();
    if (type === ResultType.Destination) {
      for (const cluster of this.clusters) {
        for (const value of cluster.placeIds) {
          if (id === value.toString()) {
            selectedCluster = cluster;
            exists = true;
          }
        }
      }
      if (exists && selectedCluster.id) {
        this.router.navigate(['/destination', selectedCluster.slug]);
        this.analyticsService.emitEvent(
          AnalyticsEvents.navigationSearchResultItem,
          {
            destinationId: selectedCluster.id,
          },
        );
      }
      // else {
      //   this.router.navigate(['/empty-page', id, name, '']);
      // }
      this.searchInput.patchValue(name);
      this.showResults = false;
    }
    if (type === ResultType.Place) {
      // this.placeApiService
      //   .getPlaces({ google_place_id: id })
      //   .pipe(first(), takeUntil(this.destroy$))
      //   .subscribe(
      //     response => {
      //       this.searchInput.patchValue(name);
      //       this.showResults = false;
      //       if (this.appConfig.settings.is_white_label) {
      //         this.router.navigate(['/places', response.slug]);
      //       } else {
      //         this.router.navigate([
      //           '/places',
      //           selectedCluster.slug,
      //           response.slug,
      //         ]);
      //       }
      //     },
      //     () => {
      //       this.searchInput.patchValue(name);
      //       this.showResults = false;
      //       // this.router.navigate(['/empty-page', id, name, '']);
      //     },
      //   );
    }
    this.eventsService.closeMobileMenuValueSet(true);
  }

  getResultImage(result): any {
    return {
      'background-image': ToolsHelper.initBackground(result.image),
    };
  }

  getSuggestionImage(suggestion): any {
    return {
      'background-image': ToolsHelper.initBackground(suggestion.image),
    };
  }

  private compileComponent(response: Array<ClusterModel>): void {
    const clusters = response.filter(cluster => {
      return cluster.launched;
    });
    this.clusters = clusters;
    for (const cluster of clusters) {
      this.suggestions.push({
        id: cluster.id.toString(),
        type: ResultType.Destination,
        title: cluster.name,
        image: cluster.image ? cluster.image : null,
        slug: cluster.slug,
      });
    }
    this.storageService.CLUSTERS = clusters;
  }
}
